'use client';

import { usePathname, useRouter } from 'next/navigation';
import { Check, Globe } from '@phosphor-icons/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { Locale } from '@/i18n/config';
import { displayLocales, localeMeta } from '@/i18n/locale-meta';
import { cn } from '@/lib/utils';

type Props = {
  locale: Locale;
  className?: string;
};

const LOCALE_COOKIE = 'NEXT_LOCALE';
// Regex must cover every supported URL slug. Update if locales change.
const LOCALE_PREFIX_REGEX = /^\/(en|fr|es|creole)(?=\/|$)/;

export function LanguageToggle({ locale, className }: Props) {
  const router = useRouter();
  const pathname = usePathname();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  const switchLocale = useCallback(
    (next: Locale) => {
      if (next === locale) {
        setOpen(false);
        return;
      }
      const newPath = pathname.replace(LOCALE_PREFIX_REGEX, `/${next}`);
      document.cookie = `${LOCALE_COOKIE}=${next}; path=/; max-age=${60 * 60 * 24 * 365}; samesite=lax`;
      setOpen(false);
      router.push(newPath);
    },
    [locale, pathname, router]
  );

  // Close on outside click + Escape
  useEffect(() => {
    if (!open) return;

    const onPointerDown = (e: MouseEvent | TouchEvent) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        buttonRef.current?.focus();
      }
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Move focus into menu when it opens
  useEffect(() => {
    if (!open || !menuRef.current) return;
    const first = menuRef.current.querySelector<HTMLButtonElement>('button[role="menuitemradio"]');
    first?.focus();
  }, [open]);

  return (
    <div ref={containerRef} className={cn('relative', className)}>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Change language. Current language: ${localeMeta[locale].englishName}`}
        className={cn(
          'flex items-center gap-1.5 px-3 py-1.5 rounded-full border transition-all',
          'border-black/10 dark:border-white/10',
          'hover:bg-black/5 dark:hover:bg-white/5',
          'text-black dark:text-white',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-red/50'
        )}
      >
        <Globe weight="bold" className="w-4 h-4" />
        <span className="text-[10px] font-black uppercase tracking-widest">
          {locale === 'creole' ? 'HT' : locale.toUpperCase()}
        </span>
      </button>

      {open ? (
        <div
          ref={menuRef}
          role="menu"
          aria-label="Select language"
          className={cn(
            'absolute right-0 mt-2 min-w-[180px] z-[200]',
            'rounded-2xl border shadow-xl overflow-hidden',
            'border-black/10 dark:border-white/10',
            'bg-white dark:bg-zinc-900',
            'backdrop-blur-xl'
          )}
        >
          <ul className="py-1.5">
            {displayLocales.map((loc) => {
              const isCurrent = loc === locale;
              return (
                <li key={loc}>
                  <button
                    type="button"
                    role="menuitemradio"
                    aria-checked={isCurrent}
                    onClick={() => switchLocale(loc)}
                    className={cn(
                      'w-full flex items-center justify-between gap-3 px-4 py-2.5',
                      'text-sm font-medium transition-colors',
                      'text-zinc-700 dark:text-zinc-200',
                      'hover:bg-black/5 dark:hover:bg-white/5',
                      'focus:outline-none focus:bg-black/5 dark:focus:bg-white/5',
                      isCurrent && 'text-brand-red dark:text-brand-gold'
                    )}
                  >
                    <span>{localeMeta[loc].nativeName}</span>
                    {isCurrent ? (
                      <Check weight="bold" className="w-4 h-4" />
                    ) : (
                      <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-400 dark:text-zinc-500">
                        {localeMeta[loc].hreflang.slice(0, 2).toUpperCase()}
                      </span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
